import {BrowserModule} from '@angular/platform-browser';
import {NgModule} from '@angular/core';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {IndexComponent} from './routes/index/index.component';
import {environment} from '../environments/environment';
import {AngularFireModule} from 'angularfire2';
import {AngularFireAuthModule} from 'angularfire2/auth';
import {AngularFireDatabaseModule} from 'angularfire2/database';
import {CustomerService} from './services/customer.service';
import {HttpClient, HttpClientModule} from '@angular/common/http';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {SharedService} from './services/shared.service';
import {TopBarComponent} from './components/top-bar/top-bar.component';
import {MaterialModule} from './material.module';
import {TranslateModule, TranslateLoader, TranslateService} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {AccountComponent} from './routes/account/account.component';
import {RequestComponent} from './routes/account/request/request.component';
import {RequestStepBarComponent} from './components/request-step-bar/request-step-bar.component';
import {FuelComponent} from './routes/account/request/fuel/fuel.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {RecipientComponent} from './routes/account/request/recipient/recipient.component';
import {LocationComponent} from './routes/account/request/location/location.component';
import {AgmCoreModule} from '@agm/core';
import {ResultsComponent} from './routes/account/request/results/results.component';
import {PaymentComponent} from './routes/account/request/payment/payment.component';
import {UserSidenavComponent} from './components/user-sidenav/user-sidenav.component';
import {ClickOutsideDirective} from './directives/click-outside.directive';
import {ErrorDialogComponent} from './components/error-dialog/error-dialog.component';
import {LottieAnimationViewModule} from 'ng-lottie';
import {LoaderLockComponent} from './components/loader-lock/loader-lock.component';
import {TextMaskModule} from 'angular2-text-mask';
import {PhonePipe} from './pipes/phone.pipe';
import {CalendarDialogComponent} from './components/calendar-dialog/calendar-dialog.component';
import {CookieService} from 'ngx-cookie-service';
import {OrderService} from './services/order.service';
import {PaymentDialogComponent} from './components/payment-dialog/payment-dialog.component';
import {NgxStripeModule} from 'ngx-stripe';
import {OrdersComponent} from './routes/account/orders/orders.component';
import {OrdersHistoryComponent} from './routes/account/orders/orders-history/orders-history.component';
import {OrderDetailsComponent} from './routes/account/orders/order-details/order-details.component';
import {OrderReceiptComponent} from './routes/account/orders/order-receipt/order-receipt.component';
import {ProfileComponent} from './routes/account/profile/profile.component';
import {RouteNameBarComponent} from './components/route-name-bar/route-name-bar.component';
import {ToFixNumberPipe} from './pipes/to-fix-number.pipe';
import {ConfirmDialogComponent} from './components/confirm-dialog/confirm-dialog.component';
import {PaymentPageComponent} from './routes/account/payment-page/payment-page.component';
import {MomentCalendarPipe} from './pipes/moment-calendar.pipe';
import {PromosComponent} from './routes/account/promos/promos.component';
import {PasswordDialogComponent} from './components/password-dialog/password-dialog.component';
import {RateDialogComponent} from './components/rate-dialog/rate-dialog.component';
import {PageNotFoundComponent} from './routes/page-not-found/page-not-found.component';
import {AuthGuardService} from './services/auth-guard.service';
import {SignupComponent} from './routes/account/request/signup/signup.component';
import {ExpiredDialogComponent} from './components/expired-dialog/expired-dialog.component';
import {PictureDialogComponent} from './components/picture-dialog/picture-dialog.component';
import {ImageCropperModule} from 'ngx-image-cropper';
import {AngularFireStorageModule} from 'angularfire2/storage';
import {CodeVerificationDialogComponent} from './components/code-verification-dialog/code-verification-dialog.component';
import {OutOfDeliveryComponent} from './components/out-of-delivery/out-of-delivery.component';
import {ForgotPasswordComponent} from './components/forgot-password/forgot-password.component';
import {RescheduleDialogComponent} from './components/reschedule-dialog/reschedule-dialog.component';
import {CountdownTimerComponent} from './components/countdown-timer/countdown-timer.component';
import {PromoClaimComponent} from './routes/promo-claim/promo-claim.component';
import {DeviceDetectorModule} from 'ngx-device-detector';

if (!environment.production) console.log('You are in ' + environment.current);

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    //Pipes
    PhonePipe,
    ToFixNumberPipe,
    MomentCalendarPipe,
    //Components
    TopBarComponent,
    RequestStepBarComponent,
    UserSidenavComponent,
    ErrorDialogComponent,
    ConfirmDialogComponent,
    LoaderLockComponent,
    CalendarDialogComponent,
    PaymentDialogComponent,
    RouteNameBarComponent,
    PasswordDialogComponent,
    RateDialogComponent,
    ExpiredDialogComponent,
    PictureDialogComponent,
    CodeVerificationDialogComponent,
    OutOfDeliveryComponent,
    ForgotPasswordComponent,
    RescheduleDialogComponent,
    CountdownTimerComponent,
    //Directives
    ClickOutsideDirective,
    //Routes Components
    IndexComponent,
    AccountComponent,
    RequestComponent,
    FuelComponent,
    RecipientComponent,
    LocationComponent,
    ResultsComponent,
    PaymentComponent,
    OrdersComponent,
    OrdersHistoryComponent,
    OrderDetailsComponent,
    OrderReceiptComponent,
    ProfileComponent,
    PaymentPageComponent,
    PromosComponent,
    PageNotFoundComponent,
    SignupComponent,
    PromoClaimComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    MaterialModule,
    TextMaskModule,
    LottieAnimationViewModule.forRoot(),
    DeviceDetectorModule.forRoot(),
    AgmCoreModule.forRoot({
      apiKey: environment.googleMapsKey.map
    }),
    TranslateModule.forRoot(
      {
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      }
    ),
    AngularFireModule.initializeApp(environment.firebaseConfig),
    NgxStripeModule.forRoot(environment.stripeKey),
    AngularFireAuthModule,
    AngularFireDatabaseModule,
    AngularFireStorageModule,
    ImageCropperModule,
    AppRoutingModule
  ],
  entryComponents: [
    ErrorDialogComponent,
    CalendarDialogComponent,
    PaymentDialogComponent,
    ConfirmDialogComponent,
    PasswordDialogComponent,
    RateDialogComponent,
    ExpiredDialogComponent,
    PictureDialogComponent,
    CodeVerificationDialogComponent,
    OutOfDeliveryComponent,
    ForgotPasswordComponent,
    RescheduleDialogComponent
  ],
  providers: [
    CustomerService,
    SharedService,
    CookieService,
    OrderService,
    AuthGuardService
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(public translate: TranslateService) {
    translate.addLangs(['en']);
    translate.setDefaultLang('en');
  }
}
